import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useBrand } from "../../context/BrandContext";
import defaultAppLogo from "../../assets/smarttt-wordmark.png";
import { resolveClubImageUrl } from "../../utils/resolveClubImageUrl";
import {
  acceptChallenge,
  createPublicBooking,
  fetchOpenChallenges,
  fetchPublicRental,
  fetchPublicSlots,
} from "../../services/rentalService";
import "../../styles/booking.css";

function formatPrice(cents, currency = "EUR") {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency,
  }).format(cents / 100);
}

function buildDurationOptions(offering) {
  if (!offering) return [60];
  if (offering.duration_options?.length) return offering.duration_options;
  const min = offering.duration_min_minutes || 60;
  const max = offering.duration_max_minutes || min;
  const options = [];
  for (let m = min; m <= max; m += 30) options.push(m);
  if (!options.includes(max)) options.push(max);
  return [...new Set(options)].sort((a, b) => a - b);
}

function sessionPriceForDuration(offering, durationMinutes) {
  if (!offering) return 0;
  return Math.round((offering.price_cents * durationMinutes) / 60);
}

export default function BookLanding() {
  const { clubCode: paramClubCode } = useParams();
  const [searchParams] = useSearchParams();
  const embed = searchParams.get("embed") === "1";
  const challengeToken = searchParams.get("challenge");
  const { t, i18n } = useTranslation();
  const { brand } = useBrand();

  const clubCode = paramClubCode || brand?.club_subdomain || brand?.club_id;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [clubData, setClubData] = useState(null);
  const [selectedOffering, setSelectedOffering] = useState(null);
  const [selectedDate, setSelectedDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [durationMinutes, setDurationMinutes] = useState(60);
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [challenges, setChallenges] = useState([]);
  const [sparringMode, setSparringMode] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [creatorName, setCreatorName] = useState("");
  const [creatorEmail, setCreatorEmail] = useState("");
  const [playerCount, setPlayerCount] = useState(2);
  const [skillHint, setSkillHint] = useState("");

  const [acceptDialog, setAcceptDialog] = useState(null);
  const [opponentName, setOpponentName] = useState("");
  const [opponentEmail, setOpponentEmail] = useState("");

  const logoSrc = resolveClubImageUrl(brand.logo_url || clubData?.brand?.logo_url) || defaultAppLogo;
  const brandName = clubData?.club?.club_name || brand.brand_name || "SmartTT Club";
  const lang = i18n.language?.slice(0, 2) || "en";

  const durationOptions = useMemo(
    () => buildDurationOptions(selectedOffering),
    [selectedOffering]
  );

  const loadData = useCallback(async () => {
    if (!clubCode) return;
    setLoading(true);
    setError("");
    try {
      const res = await fetchPublicRental(clubCode, lang);
      if (res.status !== "success") throw new Error(res.message);
      setClubData(res.data);
      const active = (res.data.offerings || []).filter((o) => o.is_active);
      if (active.length && !selectedOffering) {
        setSelectedOffering(active[0]);
        setDurationMinutes(active[0].duration_min_minutes || 60);
      }
      const ch = await fetchOpenChallenges(clubCode, lang);
      setChallenges(ch.data || []);
    } catch (err) {
      setError(err.response?.data?.message || err.message || t("rental_load_error"));
    } finally {
      setLoading(false);
    }
  }, [clubCode, lang, t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!clubCode || !selectedOffering || !selectedDate) return;
    fetchPublicSlots(clubCode, {
      offeringId: selectedOffering.offering_id,
      date: selectedDate,
      durationMinutes,
    })
      .then((res) => setSlots(res.data || []))
      .catch(() => setSlots([]));
  }, [clubCode, selectedOffering, selectedDate, durationMinutes]);

  useEffect(() => {
    if (!selectedOffering) return;
    const options = buildDurationOptions(selectedOffering);
    if (!options.includes(durationMinutes)) {
      setDurationMinutes(options[0] || selectedOffering.duration_min_minutes || 60);
    }
  }, [selectedOffering, durationMinutes]);

  const highlightedChallenge = useMemo(
    () => challenges.find((c) => c.share_token === challengeToken),
    [challenges, challengeToken]
  );

  useEffect(() => {
    if (highlightedChallenge) {
      setAcceptDialog(highlightedChallenge);
    }
  }, [highlightedChallenge]);

  const handleBook = async () => {
    if (!selectedOffering || !selectedSlot || !creatorName || !creatorEmail) {
      setError(t("rental_fill_required"));
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const res = await createPublicBooking(clubCode, {
        offering_id: selectedOffering.offering_id,
        table_id: selectedSlot.table_id,
        starts_at: selectedSlot.starts_at,
        ends_at: selectedSlot.ends_at,
        creator_name: creatorName,
        creator_email: creatorEmail,
        creator_lang: lang,
        creator_skill_hint: skillHint,
        player_count: playerCount,
        booking_mode: sparringMode ? "SPARRING" : "STANDARD",
        embed,
      });
      if (res.data?.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleAcceptChallenge = async () => {
    if (!acceptDialog || !opponentName || !opponentEmail) return;
    setSubmitting(true);
    try {
      const res = await acceptChallenge(clubCode, acceptDialog.booking_id, {
        opponent_name: opponentName,
        opponent_email: opponentEmail,
        opponent_lang: lang,
        embed,
      });
      if (res.data?.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const changeLanguage = (e) => {
    const lng = e.target.value;
    localStorage.setItem("i18nextLng", lng);
    i18n.changeLanguage(lng);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div
      className={`booking-page${embed ? " embed-mode" : ""}`}
      style={{
        "--brand-primary": brand.color_primary || "#0877BC",
        "--brand-bg": brand.color_background || "#f4f5f6",
      }}
    >
      <header className="booking-header">
        <img src={logoSrc} alt={brandName} />
        <select value={i18n.language?.slice(0, 2) || "en"} onChange={changeLanguage} className="lang-select">
          <option value="en">EN</option>
          <option value="fr">FR</option>
          <option value="de">DE</option>
          <option value="es">ES</option>
          <option value="nl">NL</option>
            <option value="pt">PT</option>
        </select>
      </header>

      <main className="booking-main">
        <div className="booking-hero">
          <h1>{t("rental_hero_title", { club: brandName })}</h1>
          <Typography color="text.secondary">{t("rental_hero_subtitle")}</Typography>
        </div>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}

        <section className="booking-section">
          <Typography variant="h6" gutterBottom>
            {t("rental_choose_package")}
          </Typography>
          <div className="booking-grid">
            {(clubData?.offerings || [])
              .filter((o) => o.is_active)
              .map((offering) => (
                <div
                  key={offering.offering_id}
                  className={`booking-card${
                    selectedOffering?.offering_id === offering.offering_id ? " selected" : ""
                  }`}
                  onClick={() => {
                    setSelectedOffering(offering);
                    setDurationMinutes(offering.duration_min_minutes || 60);
                    setSelectedSlot(null);
                    setSparringMode(false);
                  }}
                >
                  <h3>{offering.name}</h3>
                  <p>{offering.description}</p>
                  <div className="booking-price">
                    {formatPrice(offering.price_cents, offering.currency)}
                    <span style={{ fontSize: "0.85rem", fontWeight: 400 }}> / h</span>
                  </div>
                  <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                    {offering.min_players}–{offering.max_players} {t("rental_players")}
                    {" · "}
                    {t("rental_session_duration")}: {offering.duration_label || offering.duration_min_minutes}{" "}
                    {t("rental_minutes_abbr")}
                  </Typography>
                </div>
              ))}
          </div>
        </section>

        {selectedOffering && (
          <>
            <section className="booking-section">
              <Typography variant="h6" gutterBottom>
                {t("rental_choose_slot")}
              </Typography>
              <TextField
                type="date"
                label={t("rental_date")}
                value={selectedDate}
                onChange={(e) => {
                  setSelectedDate(e.target.value);
                  setSelectedSlot(null);
                }}
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 2, mr: 2 }}
              />
              <TextField
                select
                label={t("rental_duration")}
                value={durationMinutes}
                onChange={(e) => {
                  setDurationMinutes(Number(e.target.value));
                  setSelectedSlot(null);
                }}
                SelectProps={{ native: true }}
                sx={{ mb: 2, minWidth: 140 }}
                helperText={
                  selectedOffering
                    ? t("rental_estimated_price", {
                        price: formatPrice(
                          sessionPriceForDuration(selectedOffering, durationMinutes),
                          selectedOffering.currency
                        ),
                      })
                    : ""
                }
              >
                {durationOptions.map((d) => (
                  <option key={d} value={d}>
                    {d} {t("rental_minutes_abbr")}
                  </option>
                ))}
              </TextField>
              <div className="booking-slots">
                {slots.length === 0 && (
                  <Typography color="text.secondary">{t("rental_no_slots")}</Typography>
                )}
                {slots.map((slot) => (
                  <button
                    type="button"
                    key={`${slot.table_id}-${slot.starts_at}`}
                    className={`booking-slot${
                      selectedSlot?.starts_at === slot.starts_at &&
                      selectedSlot?.table_id === slot.table_id
                        ? " selected"
                        : ""
                    }`}
                    onClick={() => setSelectedSlot(slot)}
                  >
                    {slot.start_time}
                  </button>
                ))}
              </div>
            </section>

            <section className="booking-section">
              <Typography variant="h6" gutterBottom>
                {t("rental_your_details")}
              </Typography>
              <div className="booking-form-row">
                <TextField
                  label={t("rental_name")}
                  value={creatorName}
                  onChange={(e) => setCreatorName(e.target.value)}
                  fullWidth
                />
                <TextField
                  label={t("email")}
                  type="email"
                  value={creatorEmail}
                  onChange={(e) => setCreatorEmail(e.target.value)}
                  fullWidth
                />
              </div>
              <div className="booking-form-row">
                <TextField
                  label={t("rental_players_count")}
                  type="number"
                  value={playerCount}
                  onChange={(e) => setPlayerCount(Number(e.target.value))}
                  inputProps={{ min: selectedOffering.min_players, max: selectedOffering.max_players }}
                  fullWidth
                />
                {selectedOffering.allow_sparring && (
                  <FormControlLabel
                    control={
                      <Switch checked={sparringMode} onChange={(e) => setSparringMode(e.target.checked)} />
                    }
                    label={t("rental_sparring_mode")}
                  />
                )}
              </div>
              {sparringMode && (
                <TextField
                  label={t("rental_skill_hint")}
                  value={skillHint}
                  onChange={(e) => setSkillHint(e.target.value)}
                  fullWidth
                  sx={{ mb: 2 }}
                />
              )}
              <Button
                variant="contained"
                size="large"
                disabled={submitting || !selectedSlot}
                onClick={handleBook}
              >
                {submitting
                  ? t("rental_processing")
                  : sparringMode
                  ? t("rental_pay_half_challenge")
                  : t("rental_book_and_pay")}
              </Button>
            </section>
          </>
        )}

        {challenges.length > 0 && (
          <section className="booking-section">
            <Typography variant="h6" gutterBottom>
              {t("rental_open_challenges")}
            </Typography>
            <div className="challenge-list">
              {challenges.map((ch) => (
                <div key={ch.booking_id} className="challenge-item">
                  <div>
                    <strong>{ch.creator_name}</strong>
                    {ch.creator_skill_hint && ` · ${ch.creator_skill_hint}`}
                    <Typography variant="body2" color="text.secondary">
                      {new Date(ch.starts_at).toLocaleString(lang)} · {ch.offering?.name}
                    </Typography>
                  </div>
                  <Button variant="outlined" onClick={() => setAcceptDialog(ch)}>
                    {t("rental_accept_challenge")}
                  </Button>
                </div>
              ))}
            </div>
          </section>
        )}
      </main>

      <footer className="booking-footer">{t("rental_powered_by")}</footer>

      <Dialog open={!!acceptDialog} onClose={() => setAcceptDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{t("rental_accept_challenge")}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {t("rental_accept_description")}
          </Typography>
          <TextField
            label={t("rental_name")}
            value={opponentName}
            onChange={(e) => setOpponentName(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
          />
          <TextField
            label={t("email")}
            type="email"
            value={opponentEmail}
            onChange={(e) => setOpponentEmail(e.target.value)}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAcceptDialog(null)}>{t("cancel")}</Button>
          <Button variant="contained" onClick={handleAcceptChallenge} disabled={submitting}>
            {t("rental_pay_half_accept")}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
